import React, { useEffect } from 'react';
import { Form, Input, Button, Card, message, Upload, Select } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { addShop, updateShop } from '../store/shopsSlice';
import { formatName, formatMobile } from '../utils/formatters';
import { API_URL } from '../utils/api';

const ShopForm = () => {
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();
  const { shops } = useSelector((state) => state.shops);

  const isEditMode = !!id;

  useEffect(() => {
    if (isEditMode) {
      const shop = shops.find((s) => s.id === id);
      if (shop) {
        // Prepare initial values
        const initialValues = { ...shop };
        if (shop.boardImageUrl) {
          initialValues.boardImage = [{
            uid: '-1',
            name: 'Existing Image',
            status: 'done',
            url: shop.boardImageUrl.startsWith('http') ? shop.boardImageUrl : `${API_URL}${shop.boardImageUrl}`,
          }];
        }
        form.setFieldsValue(initialValues);
      } else {
        message.error('Shop not found');
        navigate('/admin/shops');
      }
    }
  }, [id, isEditMode, shops, form, navigate]);

  const normFile = (e) => {
    if (Array.isArray(e)) {
      return e;
    }
    return e?.fileList;
  };

  const onFinish = (values) => {
    const formData = new FormData();

    // Append standard fields
    formData.append('name', formatName(values.name));
    formData.append('ownerName', formatName(values.ownerName));
    const mobile = formatMobile(values.ownerNumber);
    formData.append('ownerNumber', mobile);
    formData.append('contactNumber', mobile); // Keeping both as per original
    formData.append('pincode', values.pincode);
    if (values.gstNo) formData.append('gstNo', values.gstNo);
    formData.append('shopType', values.shopType || 'General Store');
    formData.append('address', values.address);
    if (values.location) formData.append('location', values.location);

    // Handle Image
    if (values.boardImage && values.boardImage.length > 0) {
      const file = values.boardImage[0].originFileObj;
      if (file) {
        formData.append('boardImage', file);
      }
    }

    if (isEditMode) {
      dispatch(updateShop({ id, data: formData }))
        .unwrap()
        .then(() => {
          message.success('Shop updated successfully');
          navigate('/admin/shops');
        })
        .catch((err) => message.error('Failed to update shop: ' + err));
    } else {
      formData.append('id', `SHOP-${Date.now()}`);
      formData.append('createdAt', new Date().toISOString());

      dispatch(addShop(formData))
        .unwrap()
        .then(() => {
          message.success('Shop created successfully');
          navigate('/admin/shops');
        })
        .catch((err) => message.error('Failed to create shop: ' + err));
    }
  };

  return (
    <Card title={isEditMode ? 'Edit Shop' : 'Add Shop'}>
      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
      >
        <Form.Item
          name="name"
          label="Shop Name"
          rules={[{ required: true, message: 'Please input the shop name!' }]}
        >
          <Input placeholder="Enter shop name" />
        </Form.Item>

        <Form.Item
          name="ownerName"
          label="Owner Name"
          rules={[{ required: true, message: 'Please input the owner name!' }]}
        >
          <Input placeholder="Enter owner name" />
        </Form.Item>

        <Form.Item
          name="ownerNumber"
          label="Owner Number"
        >
          <Input placeholder="Enter owner number" />
        </Form.Item>

        <Form.Item
          name="pincode"
          label="Pincode"
          rules={[{ required: true, message: 'Please input the pincode!' }]}
        >
          <Input placeholder="Enter pincode" />
        </Form.Item>

        <Form.Item
          name="gstNo"
          label="GST No."
        >
          <Input placeholder="Enter GST number" />
        </Form.Item>

        <Form.Item
          name="shopType"
          label="Shop Type"
          rules={[{ required: true, message: 'Please select shop type' }]}
          initialValue="General Store"
        >
          <Select>
            <Select.Option value="General Store">General Store</Select.Option>
            <Select.Option value="Kiran Store">Kiran Store</Select.Option>
            <Select.Option value="Super Market">Super Market</Select.Option>
            <Select.Option value="Cloth Shop">Cloth Shop</Select.Option>
          </Select>
        </Form.Item>

        <Form.Item
          name="address"
          label="Address"
          rules={[{ required: true, message: 'Please input the address!' }]}
        >
          <Input.TextArea rows={4} placeholder="Enter address" />
        </Form.Item>

        <Form.Item
          name="boardImage"
          label="Shop Board Image"
          valuePropName="fileList"
          getValueFromEvent={normFile}
        >
          <Upload
            name="boardImage"
            listType="picture"
            maxCount={1}
            beforeUpload={() => false}
          >
            <Button icon={<UploadOutlined />}>Click to Upload</Button>
          </Upload>
        </Form.Item>

        <Form.Item
          name="location"
          label="Shop Location"
        >
          <Input placeholder="Latitude, Longitude or area" />
        </Form.Item>

        <Form.Item>
          <Button type="primary" htmlType="submit">
            {isEditMode ? 'Update' : 'Create'}
          </Button>
          <Button style={{ marginLeft: 8 }} onClick={() => navigate('/admin/shops')}>
            Cancel
          </Button>
        </Form.Item>
      </Form>
    </Card>
  );
};

export default ShopForm;
